import Feature from 'ol/Feature';
import {Point} from 'ol/geom';
import {Icon, Style} from 'ol/style';

/**
 * Shared fire effect — a cluster of flickering fire icons. Reused by the
 * explosion effect (short-lived fire that fades after an impact) and by the
 * wildfire animation (persistent fire that roams inside an area).
 *
 * Placement is delegated to a `spawn()` callback returning an EPSG:3857
 * coordinate, so each caller decides whether fire scatters around a point,
 * fills a polygon, etc.
 *
 * Sizing is resolution-aware: each flame represents ~`sizeMeters` on the ground,
 * so it grows as you zoom in and shrinks as you zoom out — clamped to
 * [minPx, maxPx] on screen so it never disappears or dominates the map.
 */

const FIRE_SRC = '/images/fire.svg';
const FIRE_INTRINSIC_PX = 500;

/**
 * Spawn a cluster of flickering fire icons.
 *
 * @param layer  ol layer to draw on
 * @param options.spawn          () => [x, y] in EPSG:3857 — placement for each fire
 * @param options.count          number of fire icons (default 6)
 * @param options.duration       ms before the fire fades out and is removed;
 *                               omit / Infinity for fire that burns forever
 * @param options.tick           flicker frame interval in ms (default 120)
 * @param options.sizeMeters     real-world span a flame represents (default 1200);
 *                               larger = bigger fire that scales with zoom
 * @param options.minPx          smallest on-screen size in px (default 10)
 * @param options.maxPx          largest on-screen size in px (default 40)
 * @param options.relocateChance per-tick chance (0..1) a fire jumps to a fresh
 *                               spawn() point — makes wildfire feel alive (default 0)
 * @returns {{stop: function, features: Feature[]}} controller to stop it early
 */
export function fire(layer, options = {}) {
    const {
        spawn,
        count = 6,
        duration = Infinity,
        tick = 120,
        sizeMeters = 1200,
        minPx = 10,
        maxPx = 40,
        relocateChance = 0
    } = options;

    function flickerStyle(lifeLeft) {
        const flick = 0.5 + Math.random() * 0.3;
        const opacity = Math.min(1, lifeLeft + 0.2) * (0.6 + Math.random() * 0.4);
        return (feature, resolution) => {
            const px = Math.max(minPx, Math.min(maxPx, sizeMeters / resolution)) * flick;
            return new Style({
                image: new Icon({
                    src: FIRE_SRC,
                    scale: px / FIRE_INTRINSIC_PX,
                    opacity
                })
            });
        };
    }

    const fires = [];
    for (let i = 0; i < count; i++) {
        const f = new Feature(new Point(spawn()));
        f.set('selectable', false);
        f.setStyle(flickerStyle(1));
        fires.push(f);
        layer.getSource().addFeature(f);
    }

    function remove() {
        fires.forEach(f => layer.getSource().removeFeature(f));
    }

    let elapsed = 0;
    const interval = setInterval(() => {
        elapsed += tick;
        const lifeLeft = duration === Infinity ? 1 : 1 - elapsed / duration;
        if (lifeLeft <= 0) {
            clearInterval(interval);
            remove();
            return;
        }
        fires.forEach(f => {
            if (relocateChance && Math.random() < relocateChance) {
                f.setGeometry(new Point(spawn()));
            }
            f.setStyle(flickerStyle(lifeLeft));
        });
    }, tick);

    return {
        stop() {
            clearInterval(interval);
            remove();
        },
        features: fires
    };
}
